import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Button,
	Dialog,
	Form,
	FormItem,
	InputRow,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Select,
	SelectItem,
	TextArea,
	TextAreaReadOnly,
	TextInput,
	TextInputReadOnly,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useDataInputDialog, useSynchronizedRef } from "client/Hooks";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	IntegrationFlowSettingSaveModel,
	IntegrationSettingScopeKind
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IntegrationSharedSettingOptionModel } from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { useInfoPlaceHoldersDialog } from "./InfoPlaceHoldersDialog";
import { useSharedSettingDialog } from "./SharedSettingDialog";
import { ensureNotNil, isNil } from "client/Misc/utils";
import {
	DataSourceSortDirection,
	nativeSortFunction
} from "client/Data/sorting";
import { ClientValidationErrorModel } from "client/Api/common";

export type SettingsDialogProps = {
	appId: number;
	initialData?: IntegrationFlowSettingSaveModel;
	sharedSettings: readonly IntegrationSharedSettingOptionModel[];
	existingKeys: readonly string[];
	existingNames: readonly string[];
	serverErrors: readonly ClientValidationErrorModel[];
	isReadOnly?: boolean;
	onSharedSettingCreated: (sharedSetting: IntegrationSharedSettingOptionModel) => void;
	onClose: () => void;
	onSave: (data: IntegrationFlowSettingSaveModel) => void;
};

export const SettingsDialog = (props: SettingsDialogProps): ReactElement => {
	const {
		appId,
		initialData,
		sharedSettings,
		existingKeys,
		existingNames,
		serverErrors,
		isReadOnly = false,
		onSharedSettingCreated,
		onClose,
		onSave
	} = props;

	const [model, setModel] = useState<SettingFormData>(() => convertToFormData(initialData));

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<SettingFormData> => {
			const result: IValidationConfiguration<SettingFormData> = staticSettingsValidationConfiguration.clone();

			result.forScalar("key")
				.add(
					inline<string>((value) =>
						existingKeys.includes(value)
							? {
								kind: InputValidationResultKind.Invalid,
								errorMessage: localize("_IsAlreadyBeingUsedAndMustBeUnique")
							}
							: { kind: InputValidationResultKind.Valid }
					)
				);

			result.when(x => x.scopeKind === IntegrationSettingScopeKind.ThisFlow)
				.forScalar("displayName")
				.add(
					inline<string>((value) =>
						existingNames.some(
							x => x.toLocaleLowerCase() === value.toLocaleLowerCase()
						)
							? {
								kind: InputValidationResultKind.Invalid,
								errorMessage: localize("_IsAlreadyBeingUsedAndMustBeUnique")
							}
							: { kind: InputValidationResultKind.Valid }
					)
				);

			return result;
		},
		[existingKeys, existingNames]
	);

	const formHandler: IFormHandler<SettingFormData> = useFormHandler<SettingFormData>(
		model,
		setModel,
		validationConfiguration
	);

	const stableFormHandler: ReadonlyRefObject<IFormHandler<SettingFormData>> = useSynchronizedRef(formHandler);

	const setServerErrorsHandler = useCallback(
		(errors: readonly ClientValidationErrorModel[]): void => {
			stableFormHandler.current
				.validation
				.clearServerErrors();

			stableFormHandler.current
				.validation
				.setServerErrors(convertServerErrors(errors));
		},
		[stableFormHandler]
	);

	useEffect(
		(): void => {
			// NOTE: Due to a bug in our validation infrastructure if we set server errors
			// directly after user opens the dialog, they will be overwritten by some queue update
			setTimeout(
				() => setServerErrorsHandler(serverErrors),
				1
			);
		},
		[
			serverErrors,
			setServerErrorsHandler
		]
	);

	const [infoDialog, openInfoDialog] = useInfoPlaceHoldersDialog();
	const [sharedSettingDialog, openSharedSettingDialog] = useSharedSettingDialog();

	const sharedSettingOptions = useMemo(
		(): readonly SelectItem<number>[] => sharedSettings
			.map((x): SelectItem<number> => ({
				value: x.id,
				text: x.displayName
			}))
			.sort(
				(a, b) => nativeSortFunction(
					a.text,
					b.text,
					DataSourceSortDirection.Ascending
				)
			),
		[sharedSettings]
	);

	const createSharedSettingHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<IntegrationSharedSettingOptionModel> = await openSharedSettingDialog({
				appId,
				existingNames: sharedSettings.map(x => x.displayName)
			});

			if (!result.wasSuccessful) {
				return;
			}

			onSharedSettingCreated(result.result);

			formHandler.for("sharedSettingId")
				.attachToSelect()
				.onChange(
					result.result
						.id
				);

			formHandler.for("sharedSettingId")
				.validation
				.triggerInputFinished();
		},
		[
			appId,
			formHandler,
			onSharedSettingCreated,
			openSharedSettingDialog,
			sharedSettings
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const validationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			onSave(prepareForSaving(model));
		},
		[
			formHandler,
			model,
			onSave
		]
	);

	return (
		<>
			<Dialog
				trackingId="integration-settings-dialog"
			>
				<Dialog.Header
					title={localize(isNil(initialData) ? "_NewSetting" : "_EditSetting")}
					onShowInfo={() => openInfoDialog({})}
					onClose={onClose}
				/>
				<Dialog.Body>
					<Form onSubmit={isReadOnly ? "NONE" : saveHandler}>
						<FormItem label={localize("_Key")} isRequired>
							{isReadOnly
								? (
									<TextInputReadOnly
										{...formHandler.for("key").attachToTextInput()}
									/>
								)
								: (
									<>
										<TextInput
											{...formHandler.for("key")
												.attachToTextInput()
											}
											placeholder={localize("_EnterKey")}
										/>
										<ValidationError
											{...formHandler.for("key").attachToValidationError()}
										/>
									</>
								)
							}
							<FormItem.Description>
								{localize("_SettingKeyHelperText")}
							</FormItem.Description>
						</FormItem>
						<FormItem label={localize("_Scope")} isForGroup>
							<RadioButtonGroup
								{...formHandler.for("scopeKind")
									.attachToRadioButtonGroup()
								}
								options={scopeOptions}
								isDisabled={isReadOnly}
							/>
						</FormItem>
						{formHandler.when(
							x => x.scopeKind === IntegrationSettingScopeKind.ThisFlow,
							(attachable) => (
								<>
									<FormItem label={localize("_Value")} isRequired>
										{isReadOnly
											? (
												<TextInputReadOnly
													{...attachable.for("value").attachToTextInput()}
												/>
											)
											: (
												<>
													<TextInput
														{...attachable.for("value")
															.attachToTextInput()
														}
														placeholder={localize("_EnterValue")}
													/>
													<ValidationError
														{...attachable.for("value").attachToValidationError()}
													/>
												</>
											)
										}
									</FormItem>
									<FormItem label={localize("_DisplayName")} isRequired>
										{isReadOnly
											? (
												<TextInputReadOnly
													{...attachable.for("displayName").attachToTextInput()}
												/>
											)
											: (
												<>
													<TextInput
														{...attachable.for("displayName")
															.attachToTextInput()
														}
														placeholder={localize("_EnterDisplayName")}
													/>
													<ValidationError
														{...attachable.for("displayName").attachToValidationError()}
													/>
												</>
											)
										}
									</FormItem>
									<FormItem label={localize("_Description")}>
										{isReadOnly
											? (
												<TextAreaReadOnly
													{...attachable.for("description").attachToTextArea()}
												/>
											)
											: (
												<>
													<TextArea
														{...attachable.for("description")
															.attachToTextArea()
														}
														placeholder={localize("_EnterDescription")}
													/>
													<ValidationError
														{...attachable.for("description").attachToValidationError()}
													/>
												</>
											)
										}
									</FormItem>
								</>
							),
							(attachable) => (
								<FormItem label={localize("_SharedSetting")} isRequired>
									<InputRow>
										<InputRow.Stretch>
											<Select
												{...attachable.for("sharedSettingId")
													.attachToSelect()
												}
												placeholder={localize("_SelectSharedSetting")}
												options={sharedSettingOptions}
												isDisabled={isReadOnly}
												menuTitle={localize("_SharedSetting")}
											/>
										</InputRow.Stretch>
										{!isReadOnly && (
											<Button
												kind="primary"
												text={localize("_New")}
												onClick={createSharedSettingHandler}
												trackingId="new-shared-setting"
											/>
										)}
									</InputRow>
									<ValidationError
										{...attachable.for("sharedSettingId").attachToValidationError()}
									/>
								</FormItem>
							)
						)}
					</Form>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						{!isReadOnly && (
							<Toolbar.Button
								kind="primary"
								text={localize("_Save")}
								onClick={saveHandler}
								trackingId="save"
							/>
						)}
						<Toolbar.Button
							kind="secondary"
							text={localize("_Close")}
							onClick={onClose}
							trackingId="close"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{infoDialog}
			{sharedSettingDialog}
		</>
	);
};
SettingsDialog.displayName = "SettingsDialog";

export type SettingsDialogInitialData = Omit<
	SettingsDialogProps,
	| "sharedSettings"
	| "onSharedSettingCreated"
	| "onClose"
	| "onSave"
>;

export function useSettingsDialog(
	sharedSettings: readonly IntegrationSharedSettingOptionModel[],
	onSharedSettingCreated: (sharedSetting: IntegrationSharedSettingOptionModel) => void
): [
	element: ReactElement | null,
	open: (props: SettingsDialogInitialData) => Promise<TryResult<IntegrationFlowSettingSaveModel>>
] {
	return useDataInputDialog<SettingsDialogInitialData, IntegrationFlowSettingSaveModel>(
		(props, onClose, onSave) => (
			<SettingsDialog
				appId={props.appId}
				initialData={props.initialData}
				sharedSettings={sharedSettings}
				existingKeys={props.existingKeys}
				existingNames={props.existingNames}
				serverErrors={props.serverErrors}
				isReadOnly={props.isReadOnly}
				onSharedSettingCreated={onSharedSettingCreated}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

export const staticSettingsValidationConfiguration = createValidationConfiguration<SettingFormData>();

staticSettingsValidationConfiguration.forScalar("key")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(255)
	);

const thisFlowValidationConfiguration = staticSettingsValidationConfiguration.when(
	x => x.scopeKind === IntegrationSettingScopeKind.ThisFlow
);

thisFlowValidationConfiguration.forScalar("displayName")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(255)
	);

thisFlowValidationConfiguration.forScalar("value")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(2_000)
	);

thisFlowValidationConfiguration.forScalar("description")
	.add(
		doesNotExceedLength(255)
	);

const sharedValidationConfiguration = staticSettingsValidationConfiguration.when(
	x => x.scopeKind === IntegrationSettingScopeKind.Shared
);

sharedValidationConfiguration.forScalar("sharedSettingId")
	.add(
		isNotNullNorUndefined()
	);


type SettingFormData = {
	key: string;
	scopeKind: IntegrationSettingScopeKind;
	value: string;
	displayName: string;
	description: string;
	sharedSettingId: number | null;
};

function convertToFormData(
	setting: IntegrationFlowSettingSaveModel | undefined
): SettingFormData {
	if (isNil(setting)) {
		return EMPTY_FORM_VALUE;
	}

	switch (setting.scopeKind) {
		case IntegrationSettingScopeKind.ThisFlow:
			return {
				key: setting.key,
				scopeKind: IntegrationSettingScopeKind.ThisFlow,
				value: setting.value,
				displayName: setting.displayName,
				description: setting.description,
				sharedSettingId: null
			};
		case IntegrationSettingScopeKind.Shared:
			return {
				...EMPTY_FORM_VALUE,
				key: setting.key,
				scopeKind: IntegrationSettingScopeKind.Shared,
				sharedSettingId: setting.sharedSettingId
			};
		default:
			return never(setting);
	}
}

function prepareForSaving(
	model: SettingFormData
): IntegrationFlowSettingSaveModel {
	switch (model.scopeKind) {
		case IntegrationSettingScopeKind.Shared:
			return {
				scopeKind: IntegrationSettingScopeKind.Shared,
				key: model.key,
				sharedSettingId: ensureNotNil(model.sharedSettingId)
			};
		case IntegrationSettingScopeKind.ThisFlow:
			return {
				scopeKind: IntegrationSettingScopeKind.ThisFlow,
				key: model.key,
				value: model.value,
				displayName: model.displayName,
				description: model.description
			};
		default:
			return never(model.scopeKind);
	}
}

const scopeOptions: readonly RadioButtonGroupItem<IntegrationSettingScopeKind>[] = [
	{
		value: IntegrationSettingScopeKind.ThisFlow,
		text: localize("_ThisFlow")
	},
	{
		value: IntegrationSettingScopeKind.Shared,
		text: localize("_UseSharedSetting")
	}
];

const EMPTY_FORM_VALUE: SettingFormData = {
	key: "",
	scopeKind: IntegrationSettingScopeKind.ThisFlow,
	value: "",
	displayName: "",
	description: "",
	sharedSettingId: null
};
